// Result types and deserialization for the barrier-analysis worker output, plus
// stable empty fallbacks used while the first worker compute is in flight.
// Pure types + data — no React.

/** Lightweight time bucket as returned by the worker (no resident report-id set). */
export interface TimeBucketMeta {
    key: string;
    label: string;
}

export interface BarrierTotal {
    success: number;
    failure: number;
}

export interface BarrierHealthEntry {
    nodeId: number;
    name: string;
    fullName: string;
    successCount: number;
    failureCount: number;
    demandCoverage: number;
    effectivenessRate: number;
    failureExposure: number;
    status: 'critical' | 'warning' | 'healthy';
}

export interface ExecutiveSummaryResult {
    totalReports: number;
    totalBarriers: number;
    criticalCount: number;
    warningCount: number;
    healthyCount: number;
    unchallengedCount: number;
    topCritical: BarrierHealthEntry[];
}

export interface UnmappedReport {
    reportId: number;
    uniqueId: string;
    dateTime: string | undefined;
    sourceName: string;
}

export interface SystemMetrics {
    passThrough: {count: number; total: number; unmapped: number; rate: number | null};
    unmappedReports: UnmappedReport[];
}

export interface BarrierTrend {
    effectivenessSlope: number | null;
    demandSlope: number | null;
    sparkline: number[];
}

/** Fully-deserialized aggregation results from the barrier-analysis worker. */
export interface AnalysisResult {
    availableSources: string[];
    barrierTotals: Map<number, BarrierTotal>;
    systemMetrics: SystemMetrics;
    executiveSummary: ExecutiveSummaryResult;
    sourceComparison: Map<string, Map<number, {success: number; failure: number; total: number}>>;
    timeBuckets: TimeBucketMeta[];
    timelineCellLookup: Map<string, {success: number; failure: number}>;
    barrierMtbf: Map<number, number | null>;
    barrierTrends: Map<number, BarrierTrend>;
}

/** Worker output shape (Maps serialized as entry arrays). */
export interface SerializedAnalysis {
    availableSources: string[];
    barrierTotals: [number, BarrierTotal][];
    systemMetrics: SystemMetrics;
    executiveSummary: ExecutiveSummaryResult;
    sourceComparison: [string, [number, {success: number; failure: number; total: number}][]][];
    timeBuckets: TimeBucketMeta[];
    timelineCellLookup: [string, {success: number; failure: number}][];
    barrierMtbf: [number, number | null][];
    barrierTrends: [number, BarrierTrend][];
}

export function deserializeAnalysis(raw: SerializedAnalysis): AnalysisResult {
    return {
        availableSources: raw.availableSources,
        barrierTotals: new Map(raw.barrierTotals),
        systemMetrics: raw.systemMetrics,
        executiveSummary: raw.executiveSummary,
        sourceComparison: new Map(raw.sourceComparison.map(([src, entries]) => [src, new Map(entries)])),
        timeBuckets: raw.timeBuckets,
        timelineCellLookup: new Map(raw.timelineCellLookup),
        barrierMtbf: new Map(raw.barrierMtbf),
        barrierTrends: new Map(raw.barrierTrends),
    };
}

// Stable empty fallbacks used while the first worker compute is in flight.
export const EMPTY_STRING_ARRAY: string[] = [];
export const EMPTY_BARRIER_TOTALS: Map<number, BarrierTotal> = new Map();
export const EMPTY_SOURCE_COMPARISON: Map<string, Map<number, {success: number; failure: number; total: number}>> = new Map();
export const EMPTY_TIME_BUCKETS: TimeBucketMeta[] = [];
export const EMPTY_TIMELINE_LOOKUP: Map<string, {success: number; failure: number}> = new Map();
export const EMPTY_BARRIER_MTBF: Map<number, number | null> = new Map();
export const EMPTY_BARRIER_TRENDS: Map<number, BarrierTrend> = new Map();
export const EMPTY_UNMAPPED_REPORTS: UnmappedReport[] = [];
export const UNMAPPED_PAGE_SIZE = 25;
export const DEFAULT_PASS_THROUGH = {count: 0, total: 0, unmapped: 0, rate: null};
export const EMPTY_EXECUTIVE_SUMMARY: ExecutiveSummaryResult = {
    totalReports: 0,
    totalBarriers: 0,
    criticalCount: 0,
    warningCount: 0,
    healthyCount: 0,
    unchallengedCount: 0,
    topCritical: [],
};
